/**
 * The versioned Reach save (M3 spec §9; plan R5): a strict Zod schema of `ReachSave`, serialisation and a parse
 * that runs the migration chain first. Loads resume at `lastSafe` until M2's docked resume replaces it (bridge).
 */
import { z } from 'zod'
import type { FactionKey } from '../../galaxy/layout/types'
import type { NavTargetKind, PartId, ReachSave, ReachState } from '../types'

export const REACH_SAVE_VERSION = 1
export const REACH_SAVE_KIND = 'eclipse.reach'

const PART_KEYS = ['hull_plating', 'electronics', 'drive_parts'] as const satisfies readonly PartId[]
const FACTION_KEYS = ['authority', 'tethys', 'meridian', 'varro_security', 'mutual', 'holdfast', 'raider'] as const satisfies readonly FactionKey[]
const NAV_KINDS = ['station', 'relay', 'poi', 'body', 'field', 'laneRing', 'hulk'] as const satisfies readonly NavTargetKind[]

// Exhaustiveness: each list above names every member of its union.
type Missing<U, L extends readonly string[]> = Exclude<U, L[number]>
const exhaustive: [Missing<PartId, typeof PART_KEYS>, Missing<FactionKey, typeof FACTION_KEYS>, Missing<NavTargetKind, typeof NAV_KINDS>] extends [never, never, never] ? true : false = true
void exhaustive

const id = z.string().min(1)
const count = z.number().int().nonnegative()
const seconds = z.number().nonnegative()
const vec3 = z.tuple([z.number(), z.number(), z.number()])
const quat = z.tuple([z.number(), z.number(), z.number(), z.number()])

const relayStateSchema = z.strictObject({
  status: z.enum(['online', 'offline', 'reboot']),
  delivered: z.partialRecord(z.enum(PART_KEYS), count),
  attempts: count,
  scanned: z.boolean(),
  sponsor: z.enum(FACTION_KEYS).nullable(),
  sponsorPending: z.boolean(),
  relinkedAt: seconds.nullable(),
})

const systemDeltaSchema = z.strictObject({
  rocks: z.record(id, z.number().min(0).max(1)),
  lootedPois: z.array(id),
  clearedBases: z.array(id),
  wrecks: z.array(z.unknown()),
})

const locationSchema = z.strictObject({ systemId: id, position: vec3, rotation: quat, stationId: id.nullable() })

const standInSchema = z.strictObject({
  credits: z.number().nonnegative(),
  cargo: z.record(z.enum(PART_KEYS), count),
  capacityT: z.number().nonnegative(),
  scannerMk: z.union([z.literal(1), z.literal(2), z.literal(3)]),
})

export const reachStateSchema = z.strictObject({
  galaxySeed: z.number().int().nonnegative().max(0xffffffff),
  gameTime: seconds,
  home: z.strictObject({ systemId: id, stationId: z.string() }),
  lastSafe: locationSchema,
  knowledge: z.record(id, z.enum(['rumoured', 'charted'])),
  discoveredPois: z.record(id, z.array(id)),
  relays: z.record(id, relayStateSchema),
  laneRings: z.record(id, z.boolean()),
  deltas: z.record(id, systemDeltaSchema),
  charts: z.strictObject({
    unsold: z.record(id, z.number().nonnegative()),
    soldTotal: z.record(id, z.number().nonnegative()),
    firstDiscoveries: z.array(id),
    /** M8 MC-a10: absent in older saves. An older build refuses a save that has it (the block is strict). */
    surveyed: z.array(id).optional(),
  }),
  route: z.array(id).nullable(),
  navTarget: z.strictObject({ kind: z.enum(NAV_KINDS), id }).nullable(),
  standIn: standInSchema.nullable(),
})

export const reachSaveSchema = z.strictObject({
  kind: z.literal(REACH_SAVE_KIND),
  version: z.literal(REACH_SAVE_VERSION),
  savedAt: z.iso.datetime(),
  state: reachStateSchema,
})

/** A snapshot of `state` (deep-copied, so later mutation does not change it). */
export function serializeReach(state: ReachState, now: Date): ReachSave {
  return { kind: REACH_SAVE_KIND, version: REACH_SAVE_VERSION, savedAt: now.toISOString(), state: structuredClone(state) }
}

/** Migrations by the version they upgrade FROM: `migrations[n]` turns a vN save into a v(n+1) save. */
export type ReachMigrations = Readonly<Record<number, (old: unknown) => unknown>>

/** Empty for v1; the first format change adds `1: (v1) => v2`. */
export const reachMigrations: ReachMigrations = {}

export type ReachParseResult = { ok: true; save: ReachSave } | { ok: false; path: string; message: string }

function versionOf(raw: unknown): number | null {
  if (typeof raw !== 'object' || raw === null || !('version' in raw)) return null
  const v = raw.version
  return typeof v === 'number' && Number.isInteger(v) ? v : null
}

/** Runs the chain from the save's version up to `target`. Each step must yield the next version. */
export function runMigrations(
  raw: unknown,
  migrations: ReachMigrations,
  target: number = REACH_SAVE_VERSION,
): { ok: true; value: unknown } | { ok: false; path: string; message: string } {
  let v = versionOf(raw)
  if (v === null) return { ok: false, path: 'version', message: 'The save has no version' }
  if (v > target) return { ok: false, path: 'version', message: `Save version ${v} is newer than this build (${target})` }
  let value = raw
  while (v < target) {
    const step = migrations[v]
    if (!step) return { ok: false, path: 'version', message: `No migration from save version ${v}` }
    try {
      value = step(value)
    } catch (err) {
      return { ok: false, path: 'version', message: `Migration from version ${v} failed: ${err instanceof Error ? err.message : String(err)}` }
    }
    const next = versionOf(value)
    if (next !== v + 1) return { ok: false, path: 'version', message: `Migration from version ${v} produced version ${String(next)}` }
    v = next
  }
  return { ok: true, value }
}

/** `a.b.0.c`, or `(root)` for the whole value. */
export function formatPath(path: readonly PropertyKey[]): string {
  return path.length === 0 ? '(root)' : path.map((p) => String(p)).join('.')
}

/** Migrates, then validates. Never throws; a failure names the first bad field's path. */
export function parseReachSave(json: unknown, migrations: ReachMigrations = reachMigrations): ReachParseResult {
  const migrated = runMigrations(json, migrations)
  if (!migrated.ok) return migrated
  const result = reachSaveSchema.safeParse(migrated.value)
  if (result.success) {
    const save: ReachSave = result.data
    return { ok: true, save }
  }
  const issue = result.error.issues[0]
  return { ok: false, path: formatPath(issue.path), message: issue.message }
}
